import React, { useCallback, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { BottomBarProvider, TableContext } from "@swiggy-private/portal-dineout-context";
import type { IPinVerificationCardData } from "@swiggy-private/portal-dineout-context";
import { ErrorComponent } from "@swiggy-private/portal-dineout-ui";
import { Toast } from "components/Toast";
import { useLiveNotificationHandler } from "hooks/useLiveNotificationHandler";
import { useTableConfig } from "hooks/useTableConfig";
import { useRouter } from "next/compat/router";
import BottomBar from "../components/BottomBar";

// Lives under TableContext.Provider so the hook has access to tableId /
// restaurantId / parentOrderId when responding to Live Notification CTAs.
const LiveNotificationsMount: React.FC = () => {
    useLiveNotificationHandler();
    return null;
};

const QrLayout: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const router = useRouter();
    const { tableConfig, isLoading, updateParentOrderId } = useTableConfig();
    const [verifyPinFlow, setVerifyPinFlow] = useState<boolean>(false);
    const [pendingVerifyCallback, setPendingVerifyCallbackState] = useState<(() => void | Promise<unknown>) | null>(
        null,
    );
    const [pinVerificationCardData, setPinVerificationCardData] = useState<IPinVerificationCardData | null>(null);
    const [, setHasBottomBar] = useState<boolean>(true);
    const isCurtainRaiserShown = true;

    // Wrap so callers pass the closure directly instead of the updater form of setState.
    const setPendingVerifyCallback = useCallback((cb: (() => void | Promise<unknown>) | null) => {
        setPendingVerifyCallbackState(() => cb);
    }, []);

    if (!router?.isReady || isLoading) {
        return <></>;
    }

    if (isEmpty(tableConfig.tableId) || isEmpty(tableConfig.restaurantId)) {
        return (
            <ErrorComponent
                onCtaClick={() => {}}
                showCta={false}
                heading={"Your session is not active"}
                description={"Please scan again to continue"}
                showFullPage={true}
            />
        );
    }

    return (
        <>
            <BottomBarProvider>
                <TableContext.Provider
                    value={{
                        ...tableConfig,
                        verifyPinFlow,
                        setVerifyPinFlow,
                        updateParentOrderId,
                        pendingVerifyCallback,
                        setPendingVerifyCallback,
                        pinVerificationCardData,
                        setPinVerificationCardData,
                    }}
                >
                    <LiveNotificationsMount />
                    {<>{children}</>}
                    <Toast />
                    <BottomBar setHasBottomBar={setHasBottomBar} isCurtainRaiserShown={isCurtainRaiserShown} />
                </TableContext.Provider>
            </BottomBarProvider>
        </>
    );
};

export default QrLayout;
